import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface MasterKillSwitchStateResponse {
  isEnabled?: boolean;
  enabled?: boolean;
  data?: { isEnabled?: boolean; enabled?: boolean } | boolean;
  success?: boolean;
}

export type KillSwitchType = 'Master' | 'Pool';

export interface KillSwitchLogEntry {
  userDisplayName?: string | null;
  updatedBy?: string | null;
  updatedAt: string;
  switchType: KillSwitchType;
  poolName: string | null;
  distroId?: string | null;
  previousState: boolean;
  recentState: boolean;
}

export interface KillSwitchLogsResponse {
  page: number;
  pageSize: number;
  totalCount: number;
  data: KillSwitchLogEntry[];
}

export interface KillSwitchLogsQuery {
  page: number;
  pageSize: number;
  fromDate?: string | null;
  toDate?: string | null;
  switchType?: KillSwitchType | null;
}

// Backend contract (Comforce.API branch feat/19014-19015-19016-kill-switch-endpoints):
//   GET {apiBaseUrl}/killswitch/master                  -> current master state (defaults ON)
//   PUT {apiBaseUrl}/killswitch/master                  body: { isEnabled: boolean }
//   GET {apiBaseUrl}/killswitch/pools/{poolId}          -> per-pool state (defaults ON)
//   PUT {apiBaseUrl}/killswitch/pools/{poolId}          body: { isEnabled, poolName, distroId? }
//   GET {apiBaseUrl}/killswitch/logs?page=&pageSize=&fromDate=&toDate=&switchType=Master|Pool
// `isEnabled === true` means AI outbound calls are enabled for that scope.
@Injectable({ providedIn: 'root' })
export class KillSwitchService {

  private readonly baseUrl = `${environment.apiBaseUrl}/killswitch`;

  /** #19022 — master state stream consumed by header and any future per-pool subscribers. */
  private readonly masterStateSubject = new BehaviorSubject<boolean | null>(null);
  readonly masterState$ = this.masterStateSubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  /** Current cached master state (null until first fetch). */
  get masterState(): boolean | null {
    return this.masterStateSubject.value;
  }

  getMasterState(): Observable<boolean> {
    return this.http
      .get<MasterKillSwitchStateResponse | boolean>(`${this.baseUrl}/master`, { headers: this.authHeaders() })
      .pipe(
        map(res => this.coerceBool(res, true)),
        tap(state => this.masterStateSubject.next(state))
      );
  }

  setMasterState(isEnabled: boolean): Observable<boolean> {
    return this.http
      .put<MasterKillSwitchStateResponse | boolean>(
        `${this.baseUrl}/master`,
        { isEnabled },
        { headers: this.authHeaders() }
      )
      .pipe(
        map(res => this.coerceBool(res, isEnabled)),
        tap(state => this.masterStateSubject.next(state))
      );
  }

  getPoolState(poolId: string | number): Observable<boolean> {
    return this.http
      .get<MasterKillSwitchStateResponse | boolean>(
        `${this.baseUrl}/pools/${encodeURIComponent(String(poolId))}`,
        { headers: this.authHeaders() }
      )
      .pipe(map(res => this.coerceBool(res, true)));
  }

  setPoolState(
    poolId: string | number,
    body: { isEnabled: boolean; poolName: string; distroId?: string | null }
  ): Observable<boolean> {
    return this.http
      .put<MasterKillSwitchStateResponse | boolean>(
        `${this.baseUrl}/pools/${encodeURIComponent(String(poolId))}`,
        body,
        { headers: this.authHeaders() }
      )
      .pipe(map(res => this.coerceBool(res, body.isEnabled)));
  }

  getLogs(query: KillSwitchLogsQuery): Observable<KillSwitchLogsResponse> {
    let params = new HttpParams()
      .set('page', String(query.page))
      .set('pageSize', String(query.pageSize));

    if (query.fromDate) params = params.set('fromDate', query.fromDate);
    if (query.toDate) params = params.set('toDate', query.toDate);
    if (query.switchType) params = params.set('switchType', query.switchType);

    return this.http
      .get<KillSwitchLogsResponse | KillSwitchLogEntry[]>(
        `${this.baseUrl}/logs`,
        { headers: this.authHeaders(), params }
      )
      .pipe(map(res => this.coerceLogs(res, query)));
  }

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({ API_KEY: environment.apiKey });
  }

  private coerceBool(
    res: MasterKillSwitchStateResponse | boolean | null | undefined,
    fallback = false
  ): boolean {
    if (res === null || res === undefined) return fallback;
    if (typeof res === 'boolean') return res;
    if (typeof res !== 'object') return fallback;
    if (typeof res.isEnabled === 'boolean') return res.isEnabled;
    if (typeof res.enabled === 'boolean') return res.enabled;
    if (typeof res.data === 'boolean') return res.data;
    if (res.data && typeof res.data === 'object') {
      if (typeof res.data.isEnabled === 'boolean') return res.data.isEnabled;
      if (typeof res.data.enabled === 'boolean') return res.data.enabled;
    }
    return fallback;
  }

  private coerceLogs(
    res: KillSwitchLogsResponse | KillSwitchLogEntry[] | null | undefined,
    query: KillSwitchLogsQuery
  ): KillSwitchLogsResponse {
    if (Array.isArray(res)) {
      return { page: query.page, pageSize: query.pageSize, totalCount: res.length, data: res };
    }
    if (res && typeof res === 'object' && Array.isArray((res as KillSwitchLogsResponse).data)) {
      const r = res as KillSwitchLogsResponse;
      return {
        page: r.page ?? query.page,
        pageSize: r.pageSize ?? query.pageSize,
        totalCount: r.totalCount ?? r.data.length,
        data: r.data
      };
    }
    return { page: query.page, pageSize: query.pageSize, totalCount: 0, data: [] };
  }
}
